<template>
  <div class="overview-last-posts ov-card">
    <div class="ov-block-title">
      <i class="material-icons ov-title-icon">history</i>
      <Tooltip :text="$t('dash.overview.help.last.title')" position="top">
        <h2 class="ov-help-title">{{ $t('dash.overview.last.title') }}<i class="material-icons ov-help-icon">info_outline</i></h2>
      </Tooltip>
      <span v-if="lastPosts.length" class="ov-chip-muted">{{ lastPosts.length }}</span>
      <button class="ov-arrow-link ov-block-link" @click="$emit('go', { name: 'posts2', tab: 'list' })">{{ $t('dash.button.all') }}<i class="material-icons">arrow_forward</i></button>
    </div>
    <div v-if="loading" class="ov-skeleton-list">
      <div v-for="n in 5" :key="n" class="ov-skeleton-line"></div>
    </div>
    <p v-else-if="!lastPosts.length" class="ov-empty">{{ $t('dash.overview.empty') }}</p>
    <ul v-else class="last-list">
      <li v-for="post in lastPosts" :key="postKey(post)" class="last-item" @click="$emit('open-last-post', post)">
        <img v-if="post.image && !brokenImages[postKey(post)]" :src="post.image" class="last-thumb" alt="" loading="lazy" referrerpolicy="no-referrer" @error="$set(brokenImages, postKey(post), true)" />
        <div v-else class="last-thumb last-thumb--empty"><i class="material-icons">description</i></div>
        <div class="last-text">
          <div class="last-head">
            <span class="last-type" :class="`last-type--${post.post_type || 'standard'}`">{{ $t(`dash.overview.type.${post.post_type || 'standard'}`) }}</span>
            <p>{{ post.title || post.description || $t('dash.overview.posts.no_text') }}</p>
          </div>
          <small>{{ $t(`dash.overview.network.${post.network}`) }} · {{ post.page_name }} · {{ formatDate(post.posted_date || post.date) }}</small>
          <div class="last-stats" v-if="statsOf(post)">
            <span v-if="statsOf(post).loading && !statsOf(post).metrics" class="last-stats-loading"><i class="material-icons spin">sync</i></span>
            <template v-else-if="statsOf(post).metrics">
              <Tooltip v-for="m in statsOf(post).metrics" :key="m.key" :text="m.label" position="top">
                <span class="last-stat"><i class="material-icons">{{ m.icon }}</i>{{ compact(m.value) }}</span>
              </Tooltip>
            </template>
            <span v-else class="last-stat last-stat--na">{{ $t('dash.overview.last.no_stats') }}</span>
          </div>
        </div>
        <div class="last-links" @click.stop>
          <Tooltip :text="$t('dash.overview.last.open_post')" position="top">
            <button class="ov-icon-btn" @click="$emit('open-last-post', post)"><i class="material-icons">open_in_browser</i></button>
          </Tooltip>
          <Tooltip v-if="hasNetworkLink(post)" :text="$t('dash.overview.last.open_network', { network: $t(`dash.overview.network.${post.network}`) })" position="top">
            <button class="ov-icon-btn" @click="$emit('open-network', post)"><i class="material-icons">open_in_new</i></button>
          </Tooltip>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import moment from 'moment'
import Tooltip from '../tooltip/tooltipInit.vue'

export default {
  name: 'OverviewLastPosts',

  components: { Tooltip },

  props: {
    last: { type: Object, default: null },
    lastStats: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false }
  },

  data() {
    return {
      brokenImages: {}
    }
  },

  computed: {
    lastPosts() {
      return (this.last && this.last.posts) || []
    }
  },

  methods: {
    postKey(post) {
      return `${post.news_id}-${post.bot_uid || post.page_id}`
    },

    // Mêmes conditions que l'option « Voir le post » du drawer
    hasNetworkLink(post) {
      if (!post.network_post_id || post.post_type === 'story') return false
      if (!['facebook', 'twitter', 'linkedin', 'pinterest', 'google', 'youtube', 'threads', 'bluesky'].includes(post.network)) return false
      return !(post.network === 'linkedin' && !/^[0-9]+$/.test(post.page_network_id || ''))
    },

    statsOf(post) {
      return this.lastStats[this.postKey(post)] || null
    },

    compact(v) {
      const n = Number(v) || 0
      if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M'
      if (n >= 10000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'k'
      return n.toLocaleString()
    },

    formatDate(date) {
      return moment(date).format('DD/MM/YYYY HH:mm')
    }
  }
}
</script>

<style scoped>
.last-list {
  list-style: none;
  margin: 0;
  padding: 0 4px 0 0;
  max-height: 560px;
  overflow-y: auto;
}
.last-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 8px;
  border-radius: 10px;
  cursor: pointer;
}
.last-item:hover {
  background: #f6f1fa;
}
.last-thumb {
  width: 44px;
  height: 44px;
  border-radius: 5.6px;
  object-fit: cover;
  flex-shrink: 0;
}
.last-thumb--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f1edf5;
  color: #6b1f4d;
}
.last-text {
  flex: 1;
  min-width: 0;
}
.last-head {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.last-type {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  border-radius: 20px;
  padding: 1px 7px;
  background: #f1f5f9;
  color: #475569;
}
/* Palette des badges de type du Reporting (ReportingContent.vue) ; standard = lien/snippet */
.last-type--image { background: #dcfce7; color: #166534; }
.last-type--video { background: #dbeafe; color: #1e40af; }
.last-type--reel { background: #ede9fe; color: #5b21b6; }
.last-type--standard { background: #ffedd5; color: #9a3412; }
.last-type--story { background: #f3e8ff; color: #6b21a8; }
.last-type--carousel { background: #cffafe; color: #164e63; }
.last-type--document { background: #fef9c3; color: #854d0e; }
.last-type--poll { background: #fce7f3; color: #9d174d; }
.last-text p {
  min-width: 0;
  font-size: 13px;
  color: #121826;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.last-text small {
  font-size: 11px;
  color: #9ba3b7;
}
.last-links {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
  opacity: 0.85;
}
.last-item:hover .last-links {
  opacity: 1;
}
.last-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}
.last-stat {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  color: #6b1f4d;
  background: #f1edf5;
  border-radius: 20px;
  padding: 2px 8px;
}
.last-stat .material-icons {
  font-size: 13px;
}
.last-stat--na {
  font-weight: 500;
  color: #9ba3b7;
}
.last-stats-loading .material-icons {
  font-size: 15px;
  color: #ab6e93;
}
</style>
